import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';

import { getEventsV2 } from '../../services/eventsV2Service';
import DataGridTable from '../../components/table/DataGridTable';
import EmptyList from '../../components/misc/EmptyList';
import RefreshButton from '../../components/misc/RefreshButton';
import EventListSearchBar from '../../components/events/EventListSearchBar';
import { EVENTS_V2_TABLE } from '../../definitions';
import { checkEventPermissions, getEventActionUrl } from '../../utils/eventListUtils';
import { gridFilterModelToApiQuery, withEventGridValueGetters } from '../../utils/eventGridColumns';

const PAGE_SIZE = 50;

const VIEW_ALL = 'all';
const VIEW_UPCOMING = 'upcoming';
const VIEW_PENDING = 'pending';
const VIEW_CANCELLED = 'cancelled';

const EventsV2List = () => {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [searchQ, setSearchQ] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const { permissions } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view');
  const listView =
    viewParam === VIEW_UPCOMING || viewParam === VIEW_PENDING || viewParam === VIEW_CANCELLED
      ? viewParam
      : VIEW_ALL;

  const columns = useMemo(() => withEventGridValueGetters(EVENTS_V2_TABLE.columns), []);

  const fetchData = useCallback(
    async ({
      model = { items: [] },
      q = '',
      page = 0,
      pageSize = PAGE_SIZE,
      view = VIEW_ALL,
    } = {}) => {
      try {
        setIsLoading(true);
        setError(null);

        const fromGrid = gridFilterModelToApiQuery(model);
        const apiFilters = {
          ...fromGrid,
          ...(q ? { q } : {}),
        };

        // Tümü / Yaklaşan / Bekleyen / İptal: hepsi upcoming tarih penceresinde (past yok)
        let status = 'upcoming';
        if (view === VIEW_UPCOMING) {
          apiFilters.isActive = 'true';
          apiFilters.isCancelled = 'false';
        } else if (view === VIEW_PENDING) {
          apiFilters.isActive = 'false';
          apiFilters.isCancelled = 'false';
        } else if (view === VIEW_CANCELLED) {
          apiFilters.isCancelled = 'true';
        }

        const result = await getEventsV2(permissions, {
          page: page + 1,
          perPage: pageSize,
          status,
          sort: 'startdate',
          order: 'asc',
          apiFilters,
        });

        setData(result.data ?? []);
        setTotal(result.total ?? 0);
      } catch (err) {
        setError(`Failed to load events: ${err.message}`);
        console.error('Error fetching events:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [permissions],
  );

  useEffect(() => {
    if (!permissions) return;
    fetchData({
      model: filterModel,
      q: searchQ,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      view: listView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    permissions,
    fetchData,
    paginationModel.page,
    paginationModel.pageSize,
    searchQ,
    filterModel,
    listView,
  ]);

  const handleViewChange = (nextView) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (nextView === VIEW_ALL) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ view: nextView }, { replace: true });
    }
  };

  const handleFilterModelChange = (model) => {
    setFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearSearch = () => {
    setSearchQ('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  const CustomTableHeader = () => (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <RefreshButton
        refreshData={() =>
          fetchData({
            model: filterModel,
            q: searchQ,
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            view: listView,
          })
        }
      />
    </GridToolbarContainer>
  );

  const permissionCheck = checkEventPermissions(permissions);
  const title =
    listView === VIEW_UPCOMING
      ? translate('list.upcoming_events', { _: 'Yaklaşan Etkinlikler' })
      : listView === VIEW_PENDING
        ? translate('list.pending_events', { _: 'Bekleyen Etkinlikler' })
        : listView === VIEW_CANCELLED
          ? translate('list.cancelled_events', { _: 'İptal Edilen Etkinlikler' })
          : translate('list.all_events', { _: 'Tüm Etkinlikler' });

  if (!permissions) {
    return <EmptyList mode="loading" />;
  }

  if (!permissionCheck.canView) {
    return (
      <EmptyList
        customMessage={translate('list.no_permission', {
          _: "You don't have the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error.",
        })}
        noCreate={true}
      />
    );
  }

  if (error && !data.length) {
    return <EmptyList customMessage={error} noCreate={true} />;
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }} p={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {listView === VIEW_UPCOMING
            ? translate('list.upcoming_events_desc', {
                _: 'Tarihi geçmemiş, yayında olan yaklaşan etkinlikler.',
              })
            : listView === VIEW_PENDING
              ? translate('list.pending_events_desc', {
                  _: 'Tarihi geçmemiş, henüz yayına alınmamış etkinlikler.',
                })
              : listView === VIEW_CANCELLED
                ? translate('list.cancelled_events_desc', {
                    _: 'Tarihi geçmemiş iptal edilmiş etkinlikler.',
                  })
                : translate('list.all_events_desc', {
                    _: 'Yaklaşan etkinlikler: yayında, bekleyen ve iptal. Geçmiş etkinlikler burada yok.',
                  })}
        </Typography>

        <EventListSearchBar
          value={searchQ}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          disabled={isLoading}
          trailing={
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="events-view-label">
                {translate('list.view', { _: 'Görünüm' })}
              </InputLabel>
              <Select
                labelId="events-view-label"
                label={translate('list.view', { _: 'Görünüm' })}
                value={listView}
                onChange={(e) => handleViewChange(e.target.value)}
              >
                <MenuItem value={VIEW_ALL}>{translate('list.view_all', { _: 'Tümü' })}</MenuItem>
                <MenuItem value={VIEW_UPCOMING}>
                  {translate('list.view_upcoming', { _: 'Yaklaşanlar' })}
                </MenuItem>
                <MenuItem value={VIEW_PENDING}>
                  {translate('list.view_pending', { _: 'Bekleyenler' })}
                </MenuItem>
                <MenuItem value={VIEW_CANCELLED}>
                  {translate('list.view_cancelled', { _: 'İptal edilenler' })}
                </MenuItem>
              </Select>
            </FormControl>
          }
        />
      </Paper>

      <Paper sx={{ flex: 1, p: 2, mt: 2 }}>
        <DataGridTable
          config={EVENTS_V2_TABLE}
          rows={data}
          columns={columns}
          loading={isLoading}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[PAGE_SIZE]}
          onRowClick={(params) => {
            window.location.href = getEventActionUrl(params.row || params.id, 'show');
          }}
          slots={{ toolbar: CustomTableHeader }}
        />
      </Paper>
    </Box>
  );
};

export default EventsV2List;
